import ListView from "./ListView";
import { Button, Modal } from "../shared";
import { useState } from "react";
import { ROOT_API_URL } from "../config/defaultSettings";
import { toast } from "react-toastify";

export const AttachmentsView = ({
  attachments,
  fileUploaded,
  onDelete,
  onReplace,
  onEdit,
  enableUpload = true,
  title = "Attachments",
  hideHeader = false,
  emptyMessage,
  previewSize = "lg",
  previewPanelClassName,
  previewContentWrapperClass = "p-3 lg:p-4",
  previewMaxHeightClass = "max-h-[80vh]",
}) => {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const imageAttachments =
    attachments?.filter((item) => item.mimetype?.startsWith("image/")) || [];

  const getApiOrigin = () =>
    String(ROOT_API_URL || "").replace(/\/api\/v1\/?$/, "");

  const stripApiV1 = (value) =>
    String(value || "").replace(/\/api\/v1\/?/, "/");

  const getAttachmentKey = (item) =>
    item?._id || item?.id || item?.filename || item?.name;

  const buildAttachmentUrl = (attachment) => {
    const apiOrigin = getApiOrigin();
    const path = attachment?.path;
    const pathHostMatch = path?.match(/\/home\/[^/]+\/([^/]+)\/uploads\//);
    const candidate = attachment?.url || attachment?.uri;

    if (candidate) {
      if (/^https?:\/\//i.test(candidate)) {
        try {
          const parsed = new URL(stripApiV1(candidate));
          const isLocalhost = ["localhost", "127.0.0.1", "0.0.0.0"].includes(
            parsed.hostname
          );
          const fallbackPath = parsed.pathname.startsWith("/uploads/")
            ? parsed.pathname
            : `/uploads${parsed.pathname}`;
          const hostFromPath = pathHostMatch?.[1]
            ? `https://${pathHostMatch[1]}`
            : "";

          if (!isLocalhost) {
            return `${parsed.origin}${fallbackPath}`.replace(
              /([^:]\/)\/+/g,
              "$1"
            );
          }

          if (hostFromPath) {
            return `${hostFromPath}${fallbackPath}`.replace(
              /([^:]\/)\/+/g,
              "$1"
            );
          }

          if (apiOrigin) {
            return `${apiOrigin}${fallbackPath}`.replace(
              /([^:]\/)\/+/g,
              "$1"
            );
          }

          return `${parsed.origin}${fallbackPath}`.replace(/([^:]\/)\/+/g, "$1");
        } catch (error) {
          return stripApiV1(candidate).replace(/([^:]\/)\/+/g, "$1");
        }
      }

      const normalized = candidate.startsWith("/") ? candidate : `/${candidate}`;
      return `${apiOrigin}${stripApiV1(normalized)}`.replace(
        /([^:]\/)\/+/g,
        "$1"
      );
    }

    if (path && path.includes("uploads/")) {
      const [, relativePath] = path.split("uploads/");
      if (relativePath) {
        if (pathHostMatch?.[1]) {
          return `https://${pathHostMatch[1]}/${relativePath}`.replace(
            /([^:]\/)\/+/g,
            "$1"
          );
        }
        return `${apiOrigin}/${relativePath}`.replace(/([^:]\/)\/+/g, "$1");
      }
    }

    if (attachment?.dir && attachment?.filename) {
      return `${apiOrigin}/${attachment.dir}/${attachment.filename}`.replace(
        /([^:]\/)\/+/g,
        "$1"
      );
    }

    return "";
  };

  const handleFileOpen = () => {
    if (!enableUpload || !fileUploaded) return;
    // Create an input element for file selection
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "*/*"; // Accept all file types
    input.multiple = true;

    // Trigger the file input click
    input.click();

    // Handle file selection
    input.onchange = (e) => {
      const selectedFiles = e.target.files;
      if (selectedFiles) {
        fileUploaded(selectedFiles);
      }
    };
  };

  const handleEdit = (item) => {};

  const handleReplace = (item) => {};

  const handleDelete = (item) => {
    if (typeof onDelete === "function") {
      onDelete(item);
    }
    setDeleteModalVisible(false);
    setSelectedItem(null);
  };

  const confirmDelete = (item) => {
    if (typeof onDelete !== "function") return;
    setSelectedItem(item);
    setDeleteModalVisible(true);
  };

  const handleImageClick = (item) => {
    const index = imageAttachments.findIndex(
      (img) => getAttachmentKey(img) === getAttachmentKey(item)
    );
    setCurrentImageIndex(index);
    setPreviewModalVisible(true);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? imageAttachments.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === imageAttachments.length - 1 ? 0 : prev + 1
    );
  };

  const buildDownloadUrl = (item) => {
    if (!item?._id) return "";
    const pathHostMatch = item?.path?.match(/\/home\/[^/]+\/([^/]+)\//);
    const candidate = item?.url || item?.uri;
    let apiBase = ROOT_API_URL;

    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        const parsed = new URL(candidate);
        const isLocalhost = ["localhost", "127.0.0.1", "0.0.0.0"].includes(
          parsed.hostname
        );
        if (!isLocalhost) {
          apiBase = `${parsed.origin}/api/v1`;
        }
      } catch (error) {
        // no-op, fallback to ROOT_API_URL
      }
    }

    if (pathHostMatch?.[1]) {
      apiBase = `https://${pathHostMatch[1]}/api/v1`;
    }

    return `${apiBase}/media/${item._id}/download`.replace(
      /([^:]\/)\/+/g,
      "$1"
    );
  };

  const handleDownload = async (item) => {
    const downloadUrl = buildDownloadUrl(item) || buildAttachmentUrl(item);
    if (!downloadUrl) {
      toast.error("No download URL available");
      return;
    }
    try {
      const response = await fetch(downloadUrl, { credentials: "include" });
      if (!response.ok) {
        throw new Error("Failed to download file");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.filename || item.name || "attachment";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  return (
    <>
      <ListView
        items={attachments}
        onClick={handleFileOpen}
        title={title}
        buttonTitle="Upload File"
        hideButton={true}
        hideHeader={hideHeader}
        emptyMessage={emptyMessage}
        subheader={
          attachments?.length > 0 && (
            <div className="border-t border-gray-200 pt-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Total Files</span>
                <span>{attachments?.length} files</span>
              </div>
            </div>
          )
        }
        layout={({ items }) => (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {items.map((item) => (
              <div
                key={getAttachmentKey(item)}
                className="flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors"
              >
                <div
                  className="relative h-28 cursor-pointer bg-gray-50"
                  onClick={() =>
                    item.mimetype?.startsWith("image/") &&
                    handleImageClick(item)
                  }
                >
                  {item.mimetype?.startsWith("image/") ? (
                    <img
                      src={buildAttachmentUrl(item)}
                      alt={item.filename || item.name || "Attachment"}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <svg
                        className="h-8 w-8 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V7a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                  )}
                  {typeof onDelete === "function" && (
                    <div className="absolute top-1 right-1">
                      <button
                        className="p-1 bg-white rounded-full shadow hover:bg-red-100"
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDelete(item);
                        }}
                      >
                        <svg
                          className="h-3 w-3 text-red-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-2 border border-gray-200 border-t-0">
                  <p className="text-xs font-medium text-gray-900 truncate">
                    {item.filename}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                      {(item.size / 1024).toFixed(2)} KB
                    </span>
                    <button
                      className="inline-flex items-center justify-center rounded-full p-1 text-teal-700 hover:text-teal-800 hover:bg-teal-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(item);
                      }}
                      title="Download"
                      aria-label="Download"
                    >
                      <svg
                        className="h-3.5 w-3.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <path d="M7 10l5 5 5-5" />
                        <path d="M12 15V3" />
                      </svg>
                    </button>
                  </div>
                  {item?.type && (
                    <div className="mt-1">
                      <span className="inline-flex items-center rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-teal-700 uppercase tracking-wide">
                        {item.type}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      />
      <Modal
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        onClose={() => setSelectedItem(null)}
        confirm={() => handleDelete(selectedItem)}
        title="Confirm Delete"
      >
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to delete "{selectedItem?.filename}"? <br />{" "}
            This action cannot be undone.
          </p>
        </div>
      </Modal>
      <Modal
        visible={editModalVisible}
        setVisible={setEditModalVisible}
        onClose={() => setEditModalVisible(false)}
        title="Edit Attachment"
      >
        {selectedItem && (
          <div className="p-4">
            <div className="mb-4">
              <label
                htmlFor="filename"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Filename
              </label>
              <input
                type="text"
                id="filename"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={selectedItem.filename}
                onChange={(e) =>
                  setSelectedItem({
                    ...selectedItem,
                    filename: e.target.value,
                  })
                }
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="alternative" onClick={() => setSelectedItem(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  handleSave(selectedItem);
                  setSelectedItem(null);
                }}
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </Modal>
      <Modal
        visible={previewModalVisible}
        setVisible={setPreviewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        size={previewSize}
        hideFooter={true}
        panelClassName={previewPanelClassName}
        contentWrapperClass={previewContentWrapperClass}
      >
        <div className="relative">
          <img
            src={buildAttachmentUrl(imageAttachments[currentImageIndex])}
            alt={imageAttachments[currentImageIndex]?.filename}
              className={`w-full h-full object-contain ${previewMaxHeightClass}`}
          />
          <button
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
            onClick={handlePrevImage}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
            onClick={handleNextImage}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </Modal>
    </>
  );
};

export default AttachmentsView;
